import { DialogDisclosure } from "@ariakit/react";
import {
  DialogTrigger,
  MinWidth,
  SafariCheck,
  useMediaQuery,
} from "@indietabletop/appkit";
import type { SpaceGits } from "@indietabletop/types";
import { useCallback } from "react";
import { Link } from "wouter";
import { button } from "../../common/common.css.ts";
import { useQuery } from "../../database.ts";
import { Gang } from "../../engine/Gang.ts";
import { EmptyState } from "../EmptyState/EmptyState.tsx";
import { FailureView } from "../failures/FailureView.tsx";
import { BuildMode } from "../fmt.ts";
import { CreateGangDialog } from "../GangFormDialog/GangFormDialog.tsx";
import { LoadingView } from "../LoadingView/LoadingView.tsx";
import { HereComeTheBastardsBanner } from "../NewsBanner/NewsBanner.tsx";
import {
  GangBuilderOnboardingCard,
  OnboardingGuard,
} from "../Onboarding/OnboardingCard.tsx";
import { Page } from "../Page/Page.tsx";
import { useResolveRuleset } from "../rulesets.tsx";
import { gang, page } from "./style.css.ts";

function GangCard(props: { data: SpaceGits.GangData }) {
  const data = props.data;
  const ruleset = useResolveRuleset(data.rulesetVersion);
  const result = ruleset.mapSuccess((ruleset) => new Gang(ruleset, data));

  if (result.isPending || result.isFailure) {
    return (
      <Link href={`/gangs/g/${data.id}`} className={gang.container}>
        <div className={gang.overline}>
          {result.isPending ? "Loading..." : "Error"}
        </div>

        <div className={gang.heading}>{data.name || Gang.FALLBACK_NAME}</div>

        <div className={gang.summary}>
          {result.isPending
            ? "Just a moment..."
            : "Failed to retrieve ruleset."}
        </div>
      </Link>
    );
  }

  const g = result.value;
  return (
    <Link href={`/gangs/g/${g.id}`} className={gang.container}>
      <div className={gang.overline}>{BuildMode.get(g.mode.type)}</div>
      <div className={gang.heading}>{g.name}</div>
      <div className={gang.summary}>{g.summary}</div>
    </Link>
  );
}

function useGangsQuery() {
  return useQuery(
    useCallback(async (db) => await db.gangs.getAll(), []),
    "gangs",
  );
}

function GangsSuccessView(props: { gangs: SpaceGits.GangData[] }) {
  const { gangs } = props;
  const isSmall = !useMediaQuery(MinWidth.MEDIUM);

  return (
    <Page backHref="~/" title="Gang Builder">
      <OnboardingGuard
        topic="gangBuilder"
        component={<GangBuilderOnboardingCard />}
      >
        <SafariCheck performCheck={gangs.length === 0}>
          <div className={page.container}>
            <HereComeTheBastardsBanner />

            {gangs.length > 0 ? (
              <div className={page.gangs}>
                {gangs.map((gang) => {
                  return <GangCard key={gang.id} data={gang} />;
                })}
              </div>
            ) : (
              <EmptyState image="butt" message="No gangs. Bummer." />
            )}

            <div className={page.actions}>
              <DialogTrigger>
                <CreateGangDialog />
                <DialogDisclosure
                  className={button({
                    size: isSmall ? "small" : "dynamic",
                    maxInlineSize: isSmall ? "auto" : "15x",
                  })}
                >
                  Create gang
                </DialogDisclosure>
              </DialogTrigger>
            </div>
          </div>
        </SafariCheck>
      </OnboardingGuard>
    </Page>
  );
}

export function GangsPage() {
  const result = useGangsQuery();

  if (result.isPending) {
    return <LoadingView />;
  }

  if (result.isFailure) {
    return <FailureView failure={result.failure} />;
  }

  return <GangsSuccessView gangs={result.value} />;
}
